import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { notebookPalette, paperSpacing } from '@/lib/notebookTheme';

export interface NotebookBackgroundProps {
  variant?: 'lined' | 'grid' | 'dotted' | 'blank' | 'graph';
  paperColor?: string;
  lineColor?: string;
  marginLine?: boolean;
  holes?: boolean;
  aging?: 'none' | 'light' | 'heavy';
  className?: string;
  children: React.ReactNode;
}

// SVG noise texture for paper aging effect
const noiseTextureSvg = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

export const NotebookBackground: React.FC<NotebookBackgroundProps> = ({
  variant = 'lined',
  paperColor = notebookPalette.paper.cream,
  lineColor = notebookPalette.elements.ruledLine,
  marginLine = true,
  holes = true,
  aging = 'light',
  className,
  children,
}) => {
  const getBackgroundStyle = (): React.CSSProperties => {
    const marginLineGradient = marginLine
      ? `, linear-gradient(90deg, transparent 79px, ${notebookPalette.elements.marginLine} 79px, ${notebookPalette.elements.marginLine} 81px, transparent 81px)`
      : '';

    switch (variant) {
      case 'lined':
        return {
          background: `
            linear-gradient(${lineColor} 1px, transparent 1px)
            ${marginLineGradient}
          `,
          backgroundSize: `100% ${paperSpacing.lined}px, 100% 100%`,
          backgroundColor: paperColor,
        };

      case 'grid':
        return {
          background: `
            linear-gradient(${notebookPalette.elements.gridLine} 1px, transparent 1px),
            linear-gradient(90deg, ${notebookPalette.elements.gridLine} 1px, transparent 1px)
          `,
          backgroundSize: `${paperSpacing.grid}px ${paperSpacing.grid}px`,
          backgroundColor: paperColor,
        };

      case 'graph':
        return {
          background: `
            linear-gradient(${notebookPalette.elements.gridLine}80 1px, transparent 1px),
            linear-gradient(90deg, ${notebookPalette.elements.gridLine}80 1px, transparent 1px),
            linear-gradient(${notebookPalette.elements.gridLine}40 1px, transparent 1px),
            linear-gradient(90deg, ${notebookPalette.elements.gridLine}40 1px, transparent 1px)
          `,
          backgroundSize: `${paperSpacing.grid * 4}px ${paperSpacing.grid * 4}px, ${paperSpacing.grid * 4}px ${paperSpacing.grid * 4}px, ${paperSpacing.grid}px ${paperSpacing.grid}px, ${paperSpacing.grid}px ${paperSpacing.grid}px`,
          backgroundColor: notebookPalette.paper.graph,
        };

      case 'dotted':
        return {
          background: `radial-gradient(circle, ${notebookPalette.elements.dotColor} 1px, transparent 1px)`,
          backgroundSize: `${paperSpacing.dotted}px ${paperSpacing.dotted}px`,
          backgroundColor: paperColor,
        };

      case 'blank':
      default:
        return {
          backgroundColor: paperColor,
        };
    }
  };

  const agingOpacity = aging === 'heavy' ? 0.06 : aging === 'light' ? 0.03 : 0;

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={getBackgroundStyle()}
    >
      {/* Paper aging texture overlay */}
      {aging !== 'none' && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: noiseTextureSvg,
            opacity: agingOpacity,
            mixBlendMode: 'multiply',
          }}
        />
      )}

      {/* Subtle paper edge shadow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: 'inset 0 0 80px rgba(0,0,0,0.03)',
        }}
      />

      {/* Binder holes */}
      {holes && (
        <div className="absolute left-3 top-0 bottom-0 flex flex-col justify-around py-16 z-10">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1 * i, type: 'spring', stiffness: 300 }}
              className="relative"
            >
              {/* Hole shadow */}
              <div
                className="absolute w-7 h-7 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, transparent 40%, rgba(0,0,0,0.1) 100%)',
                  transform: 'translate(-1px, 1px)',
                }}
              />
              {/* Main hole */}
              <div
                className="w-6 h-6 rounded-full border-2"
                style={{
                  backgroundColor: 'white',
                  borderColor: notebookPalette.elements.binderHole,
                  boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Content with margin offset when holes are visible */}
      <div className={cn('relative z-0', holes ? 'pl-12' : '')}>
        {children}
      </div>
    </div>
  );
};

// Notebook page wrapper with realistic paper edge
export const NotebookPage: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: NotebookBackgroundProps['variant'];
  rotation?: number;
}> = ({ children, className, variant = 'lined', rotation = 0 }) => {
  return (
    <motion.div
      className={cn('relative', className)}
      style={{ transform: `rotate(${rotation}deg)` }}
      whileHover={{ rotate: 0 }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      {/* Paper edge effect */}
      <div
        className="absolute -right-1 top-2 bottom-2 w-4 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.05))',
        }}
      />
      <div
        className="absolute -bottom-1 left-2 right-2 h-4 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.03))',
        }}
      />

      <NotebookBackground
        variant={variant}
        className="rounded-sm shadow-lg"
        aging="light"
      >
        {children}
      </NotebookBackground>
    </motion.div>
  );
};

export default NotebookBackground;
